import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Building2, Shield, Users, Home, FileText, Globe, ArrowRight, CheckCircle } from 'lucide-react';
import { practiceAreas } from '../data';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Building2, Shield, Users, Home, FileText, Globe,
};

export default function PracticeAreasPage() {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
      {/* Hero */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050d1a] via-[#0a1628] to-[#0f2240]" />
        <div className="absolute top-20 right-20 w-[500px] h-[500px] bg-[#c9a84c]/5 rounded-full blur-[150px]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="gold-line mb-6" />
          <h1 className="text-5xl md:text-6xl font-bold text-[#e8e4dc] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Practice <span className="text-[#c9a84c]">Areas</span>
          </h1>
          <p className="text-lg text-[#e8e4dc]/60 max-w-2xl">
            Six specialized practice areas, each led by attorneys with deep expertise. Explore our capabilities and discover how we can help with your legal matter.
          </p>
        </div>
      </section>

      {/* Practice Areas Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {practiceAreas.map((area, idx) => {
              const Icon = iconMap[area.icon] || Building2;
              return (
                <motion.div
                  key={area.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.08 }}
                >
                  <Link
                    to={`/practice-areas/${area.slug}`}
                    className="group block rounded-2xl bg-gradient-to-r from-[#0f2240]/40 to-[#0a1628]/40 border border-[#c9a84c]/10 hover:border-[#c9a84c]/30 hover:shadow-2xl hover:shadow-[#c9a84c]/5 transition-all duration-500 overflow-hidden"
                  >
                    <div className="flex flex-col lg:flex-row">
                      <div className="p-8 lg:p-10 flex-1">
                        <div className="flex items-start gap-5">
                          <div className="w-14 h-14 rounded-xl bg-[#c9a84c]/10 flex items-center justify-center shrink-0 group-hover:bg-[#c9a84c]/20 transition-colors">
                            <Icon className="w-7 h-7 text-[#c9a84c]" />
                          </div>
                          <div>
                            <h3 className="text-2xl font-bold text-[#e8e4dc] mb-3 group-hover:text-[#c9a84c] transition-colors" style={{ fontFamily: 'Playfair Display, serif' }}>
                              {area.title}
                            </h3>
                            <p className="text-[#e8e4dc]/60 leading-relaxed mb-6">{area.longDescription}</p>
                            <div className="flex flex-wrap gap-2">
                              {area.caseStudies.map((cs) => (
                                <span key={cs.id} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#c9a84c]/10 text-xs text-[#c9a84c]">
                                  <CheckCircle className="w-3 h-3" />
                                  {cs.result}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="lg:w-48 flex items-center justify-center p-6 border-t lg:border-t-0 lg:border-l border-[#c9a84c]/10">
                        <div className="flex items-center gap-2 text-sm text-[#c9a84c] font-medium group-hover:gap-3 transition-all">
                          <span>Details</span>
                          <ArrowRight className="w-4 h-4" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="p-12 rounded-2xl bg-gradient-to-b from-[#0f2240]/60 to-[#0a1628]/60 border border-[#c9a84c]/15 gold-glow">
            <h2 className="text-3xl font-bold text-[#e8e4dc] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Not Sure Which <span className="text-[#c9a84c]">Practice Area?</span>
            </h2>
            <p className="text-[#e8e4dc]/60 mb-8 max-w-lg mx-auto">
              Our attorneys will assess your situation and connect you with the right specialist. Schedule a free consultation today.
            </p>
            <Link
              to="/consultation"
              className="inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r from-[#c9a84c] to-[#b08e2e] text-[#0a1628] font-semibold rounded-xl hover:shadow-xl hover:shadow-[#c9a84c]/20 transition-all duration-300 hover:scale-105"
            >
              Get a Free Assessment
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </motion.div>
  );
}
